package com.wintermar.inspection.core.ui.util

import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import com.wintermar.inspection.core.ui.theme.LocalAppTheme

@Composable
fun String.highlightKeyword(
    keyword: String,
    highlightColor: Color = LocalAppTheme.current.color.surface.brand.primarySubtle.active,
    textColor: Color = LocalAppTheme.current.color.text.brand
): AnnotatedString {
    if (keyword.isEmpty()) return AnnotatedString(this)

    val annotatedString = AnnotatedString.Builder(this)
    var startIndex = 0

    while (true) {
        val index = this.indexOf(keyword, startIndex, ignoreCase = true)
        if (index == -1) break

        annotatedString.addStyle(
            style = SpanStyle(
                background = highlightColor,
                color = textColor,
                fontWeight = FontWeight.Bold
            ),
            start = index,
            end = index + keyword.length
        )
        startIndex = index + keyword.length
    }

    return annotatedString.toAnnotatedString()
}

@Composable
fun String.highlightKeywordWithTruncate(
    keyword: String,
    contextLength: Int = 50,
    highlightColor: Color = LocalAppTheme.current.color.surface.brand.primarySubtle.active,
    textColor: Color = LocalAppTheme.current.color.text.brand
): AnnotatedString {
    if (keyword.isEmpty()) return AnnotatedString(this)

    val keywordIndex = this.indexOf(keyword, ignoreCase = true)
    if (keywordIndex == -1) return AnnotatedString(this)  // No match found

    // Calculate context window
    val contextStart = maxOf(0, keywordIndex - contextLength)
    val contextEnd = minOf(this.length, keywordIndex + keyword.length + contextLength)

    // Build truncated text with ellipsis
    val prefix = if (contextStart > 0) "... " else ""
    val suffix = if (contextEnd < this.length) " ..." else ""

    val truncatedText = prefix + this.substring(contextStart, contextEnd) + suffix
    val adjustedKeywordStart = keywordIndex - contextStart + prefix.length
    val adjustedKeywordEnd = adjustedKeywordStart + keyword.length

    // Highlight keyword in truncated text
    val annotatedString = AnnotatedString.Builder(truncatedText)
    annotatedString.addStyle(
        style = SpanStyle(
            background = highlightColor,
            color = textColor,
            fontWeight = FontWeight.Bold
        ),
        start = adjustedKeywordStart,
        end = adjustedKeywordEnd
    )

    return annotatedString.toAnnotatedString()
}

@Composable
fun String.highlightBoldKeyword(
    keyword: String
): AnnotatedString = buildAnnotatedString {
    if (keyword.isEmpty()) {
        append(this@highlightBoldKeyword)
        return@buildAnnotatedString
    }

    val text = this@highlightBoldKeyword
    var startIndex = 0

    while (true) {
        val index = text.indexOf(keyword, startIndex, ignoreCase = true)
        if (index == -1) {
            append(text.substring(startIndex))
            break
        }
        append(text.substring(startIndex, index))
        withStyle(style = SpanStyle(fontWeight = FontWeight.Bold)) {
            append(text.substring(index, index + keyword.length))
        }

        startIndex = index + keyword.length
    }
}
